import React, {FC} from "react";
import {fixedFormat} from "../../../../utils/numberFormat.ts";
import {Font, Page, Text, View, Document, StyleSheet} from "@react-pdf/renderer";
import {TimberClass} from "../../../types/models/TimberClass.ts";
import {TimberType} from "../../../types/models/TimberType.ts";
import {Buyer} from "../../../types/models/Buyer.ts";
import {Cutting} from "../../../types/models/Cutting.ts";
import {Shipment} from "../../../types/models/Shipment.ts";
import {TimberStorage} from "../../../types/models/TimberStorage.ts";
import {WoodGroupsList} from "../../../types/models/MergeGroups.ts";
import { lightFormat } from 'date-fns';
import {Provider} from "../../../types/models/Provider.ts";

Font.register({
    family: "Roboto",
    fonts: [
        {src: "https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-light-webfont.ttf", fontWeight: 300},
        {src: "https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-regular-webfont.ttf", fontWeight: 400},
        {src: "https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-medium-webfont.ttf", fontWeight: 500},
        {src: "https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-bold-webfont.ttf", fontWeight: 600},
    ],
})

const styles = StyleSheet.create({
    page: {
        fontFamily: "Roboto",
        backgroundColor: "#ffffff",
        color: "#424242",
        paddingTop: 30,
        paddingBottom: 30,
        paddingHorizontal: 40,
        boxSizing: "border-box",
    },
    details: {
        fontSize: 10,
        marginBottom: 10,
        textAlign: "left",
    },
    info: {
        fontSize: 10,
        fontWeight: "bold",
        marginBottom: 10,
        textAlign: "left",
        marginLeft: 30,
    },
    footer: {
        position: 'absolute',
            top: 10,
            left: 40,
            right: 40,
        textAlign: 'center',
        fontSize: 7,
    },
})

const tableStyles = StyleSheet.create({
    table: {
        width: "100%",
        marginBottom: 20,
        borderRadius: 4,
        marginTop: 10,
    },
    row: {
        flexDirection: "row",
        borderBottomWidth: 1,
        borderBottomColor: "#e0e0e0",
        alignItems: "center",
        minHeight: 10,
        flexWrap: "wrap",
        ":not(:last-child)": {
            borderBottom: "none",
        },
    },
    rowBorder: {
        flexDirection: "row",
        alignItems: "center",
        minHeight: 10,
        flexWrap: "wrap",
    },
    rowHead: {
        flexDirection: "row",
    },
    cell: {
        flex: 1,
        padding: 3,
        fontSize: 8,
        color: "#424242",
        textTransform: "capitalize",
        alignItems: "center",
    },
    cellBorder: {
        flex: 1,
        padding: 3,
        fontSize: 8,
        borderBottomWidth: 1,
        borderBottomColor: "#e0e0e0",
        color: "#424242",
        textTransform: "capitalize",
        alignItems: "center",
    },
    cellRight: {
        flex: 1,
        padding: 3,
        fontSize: 8,
        color: "#424242",
        alignContent: "flex-end",
        textAlign: "right",
    },
    cellLeft: {
        flex: 1,
        padding: 3,
        fontSize: 8,
        color: "#424242",
        alignContent: "flex-start",
        textAlign: "left",
    },
    cellLeftBold: {
        flex: 1,
        padding: 3,
        fontSize: 10,
        color: "#424242",
        alignContent: "flex-start",
        textAlign: "left",
        fontWeight: "bold",
    },
    cellRightBold: {
        flex: 1,
        padding: 3,
        fontSize: 10,
        color: "#424242",
        alignContent: "flex-end",
        textAlign: "right",
        fontWeight: "bold",
    },
    headerCell: {
        padding: 3,
        fontSize: 10,
        backgroundColor: "#DAF7A6",
        fontWeight: "bold",
        overflow: "hidden",
        whiteSpace: "nowrap",
        alignItems: "center",
    },
    headerCellRight: {
        padding: 3,
        fontSize: 10,
        backgroundColor: "#DAF7A6",
        fontWeight: "bold",
        overflow: "hidden",
        whiteSpace: "nowrap",
        alignItems: "center",
        textAlign: "right",
    },
    totalCell: {
        backgroundColor: "lightgray",
    },
    innerHeading: {
        fontSize: 16,
        fontWeight: 700,
        marginBottom: 12,
        marginTop: 12,
    },
    header: {
        fontSize: 10,
        fontWeight: 700,
        marginBottom: 12,
        marginTop: 12,
        alignContent: "center",
        textAlign: "center",
    },
})

export type GroupQueryRequestPdf = {
    timberClass?: TimberClass[],
    timberType?: TimberType[],
    buyer?: Buyer,
    cutting?: Cutting[],
    storage?: TimberStorage[],
    shipment?: Shipment,
    shipmentDefined?: boolean,
    startCreate?: Date,
    endCreate?: Date,
    startShipment?: Date,
    endShipment?: Date,
}

type IGenericRootReportPdfProps = {
    title: string;
    groupedData: WoodGroupsList;
    filter: GroupQueryRequestPdf;
    provider?: Provider;
    numberOfCopies: number;
};

const GenericRootReportPdf: FC<IGenericRootReportPdfProps> = ({groupedData, title, filter, provider, numberOfCopies = 1}) => {

    return (
        <Document title={title} subject={title}>
            { [...Array(numberOfCopies).keys()].map(i => i + 1).map((pagePosition) => (
            <Page size="A4" style={styles.page} key={pagePosition}>
                {provider &&
                    <View fixed>
                        <Text style={tableStyles.header}>{provider.name} PDV ID: {provider.vatId ?? '-'}</Text>
                    </View>
                }
                <View>
                    <View wrap={true}>
                        <Text style={tableStyles.innerHeading}>{title}</Text>
                        {(filter.startCreate || filter.endCreate) &&
                            <Text style={styles.details}>
                                Datum preuzimanja:
                                {filter.startCreate && <> od {lightFormat(filter.startCreate, 'dd.MM.yyyy')}</>}
                                {filter.endCreate && <> do {lightFormat(filter.endCreate, 'dd.MM.yyyy')}</>}
                            </Text>
                        }
                        {(filter.startShipment || filter.endShipment) &&
                            <Text style={styles.details}>
                                Datum otpreme:
                                {filter.startShipment && <> od {lightFormat(filter.startShipment, 'dd.MM.yyyy')}</>}
                                {filter.endShipment && <> do {lightFormat(filter.endShipment, 'dd.MM.yyyy')}</>}
                            </Text>
                        }
                        {filter.timberType?.length &&
                            <View>
                                <Text style={styles.details}>Vrsta drveta:</Text>
                                <View style={styles.info}>
                                    <Text>
                                        {filter.timberType.map((item) => item.reportNameShort || item.name).join(', ')}
                                    </Text>
                                </View>
                            </View>
                        }
                        {filter.timberClass?.length &&
                            <View>
                                <Text style={styles.details}>Klasa:</Text>
                                <View style={styles.info}>
                                    <Text>
                                        {filter.timberClass.map((item) => item.reportNameShort || item.name).join(', ')}
                                    </Text>
                                </View>
                            </View>
                        }
                        {filter.cutting?.length &&
                            <View>
                                <Text style={styles.details}>Sječa:</Text>
                                <View style={styles.info}>
                                    <Text>
                                        {filter.cutting.map((item) => item.name).join(', ')}
                                    </Text>
                                </View>
                            </View>
                        }
                        {filter.storage?.length &&
                            <View>
                                <Text style={styles.details}>Skladište:</Text>
                                <View style={styles.info}>
                                    <Text>
                                        {filter.storage.map((item) => item.name).join(', ')}
                                    </Text>
                                </View>
                            </View>
                        }
                        {filter.shipment &&
                            <View>
                                <Text style={styles.details}>Otprema:</Text>
                                <View style={styles.info}>
                                    <Text>{filter.shipment.reportKey} ({filter.shipment.key})</Text>
                                </View>
                            </View>
                        }
                        {filter.buyer &&
                            <View>
                                <Text style={styles.details}>Kupac:</Text>
                                <View style={styles.info}>
                                    <Text>{filter.buyer.name}</Text>
                                    <Text>PDV ID: {filter.buyer.vatId ?? '-'}</Text>
                                    <Text>{filter.buyer?.addressIdAddress?.address ?? '-'}, {filter.buyer?.addressIdAddress?.city ?? '-'}</Text>
                                </View>
                            </View>
                        }
                        <View style={tableStyles.table}>
                            <View style={[tableStyles.rowHead, {justifyContent: "space-between"}]} wrap={false}>
                                <Text key="Storage" style={[tableStyles.cell, tableStyles.headerCell]}>Skladište</Text>
                                <Text key="Type" style={[tableStyles.cell, tableStyles.headerCell]}>Vrsta drveta</Text>
                                <Text key="Class" style={[tableStyles.cell, tableStyles.headerCell]}>Klasa</Text>
                                { filter.shipmentType != 'Cordwood' &&
                                    <Text key="Thickness Class" style={[tableStyles.cell, tableStyles.headerCell]}>Debljinski razred</Text>
                                }
                                { filter.shipmentType != 'Cordwood' &&
                                    <Text key="Sum Count" style={[tableStyles.cellRight, tableStyles.headerCellRight]}>Broj</Text>
                                }
                                <Text key="Sum Volume" style={[tableStyles.cellRight, tableStyles.headerCellRight]}>Drvni obujam (m&#xB3;)</Text>
                            </View>
                            {groupedData.items
                                .sort((a, b) => a.cutting.name.localeCompare(b.cutting.name))
                                .map((cuttingRow) =>
                                    <React.Fragment key={'fragment' + cuttingRow.cutting.id}>
                                    {cuttingRow.items
                                        .sort((a, b) => a.storage.name.localeCompare(b.storage.name))
                                        .map((storageRow) =>
                                        <React.Fragment key={'fragment' + storageRow.storage.id}>
                                            <View wrap={false}>
                                                <View style={tableStyles.row}>
                                                    <Text style={[tableStyles.cellLeftBold, {flex: 6}]}>
                                                        {storageRow.storage.name} ({cuttingRow.cutting.name})
                                                    </Text>
                                                </View>
                                            </View>
                                            {storageRow.items
                                                .filter(a => !!(a.items))
                                                .sort((a, b) => a.timberType.name.localeCompare(b.timberType.name) || a.timberClass.name.localeCompare(b.timberClass.name))
                                                .map((groupedRow, groupedRowIndex) =>
                                                <React.Fragment
                                                    key={'fragment' + storageRow.storage.id + groupedRow.timberClass.id + groupedRow.timberType.id}>
                                                    {groupedRow.items && groupedRow.items
                                                        .sort((a, b) => (a.thicknessClass.minDiameter || 0) - (b.thicknessClass.minDiameter || 0))
                                                        .map((itemRow, itemRowIndex) =>
                                                            itemRowIndex === 0 && groupedRowIndex === 0 ?
                                                                <View wrap={false} key={'view' + storageRow.storage.id + groupedRow.timberClass.id + groupedRow.timberType.id + itemRow.thicknessClass.id}>
                                                                    <View style={tableStyles.row}>
                                                                        <Text style={tableStyles.cellLeft}></Text>
                                                                        <Text
                                                                            style={tableStyles.cellLeft}>{groupedRow.timberClass.reportNameShort || groupedRow.timberClass.name}</Text>
                                                                        <Text
                                                                            style={tableStyles.cellLeft}>{groupedRow.timberType.reportNameShort || groupedRow.timberType.name}</Text>
                                                                        { filter.shipmentType != 'Cordwood' &&
                                                                            <Text style={tableStyles.cellLeft}>{itemRow.thicknessClass.reportNameShort || itemRow.thicknessClass.name}</Text>
                                                                        }
                                                                        { filter.shipmentType != 'Cordwood' &&
                                                                            <Text style={tableStyles.cellRight}>{itemRow.count ?? '-'}</Text>
                                                                        }
                                                                        <Text style={tableStyles.cellRight}>{fixedFormat(itemRow.sum)}</Text>
                                                                    </View>
                                                                </View>
                                                                :
                                                                itemRowIndex === 0 ?
                                                                    <View wrap={false} key={'view' + storageRow.storage.id + groupedRow.timberClass.id + groupedRow.timberType.id + itemRow.thicknessClass.id}>
                                                                        <View style={tableStyles.row}>
                                                                            <Text style={tableStyles.cellLeft}>&nbsp;</Text>
                                                                            <Text
                                                                                style={tableStyles.cellLeft}>{groupedRow.timberClass.reportNameShort || groupedRow.timberClass.name}</Text>
                                                                            <Text
                                                                                style={tableStyles.cellLeft}>{groupedRow.timberType.reportNameShort || groupedRow.timberType.name}</Text>
                                                                            { filter.shipmentType != 'Cordwood' &&
                                                                                <Text style={tableStyles.cellLeft}>{itemRow.thicknessClass.reportNameShort || itemRow.thicknessClass.name}</Text>
                                                                            }
                                                                            { filter.shipmentType != 'Cordwood' &&
                                                                                <Text style={tableStyles.cellRight}>{itemRow.count ?? '-'}</Text>
                                                                            }
                                                                            <Text style={tableStyles.cellRight}>{fixedFormat(itemRow.sum)}</Text>
                                                                        </View>
                                                                    </View>
                                                                    :
                                                                    <View wrap={false} key={'view' + storageRow.storage.id + groupedRow.timberClass.id + groupedRow.timberType.id + itemRow.thicknessClass.id}>
                                                                        <View style={tableStyles.row}>
                                                                            <Text style={tableStyles.cellLeft}>&nbsp;</Text>
                                                                            <Text style={tableStyles.cellLeft}>&nbsp;</Text>
                                                                            <Text style={tableStyles.cellLeft}>&nbsp;</Text>
                                                                            { filter.shipmentType != 'Cordwood' &&
                                                                                <Text style={tableStyles.cellLeft}>{itemRow.thicknessClass.reportNameShort || itemRow.thicknessClass.name}</Text>
                                                                            }
                                                                            { filter.shipmentType != 'Cordwood' &&
                                                                                <Text style={tableStyles.cellRight}>{itemRow.count ?? '-'}</Text>
                                                                            }
                                                                            <Text style={tableStyles.cellRight}>{fixedFormat(itemRow.sum)}</Text>
                                                                        </View>
                                                                    </View>
                                                        )}
                                                    <View wrap={false}
                                                          key={'fragment' + storageRow.storage.id + groupedRow.timberClass.id + groupedRow.timberType.id}>
                                                        <View style={tableStyles.row}>
                                                            <Text style={tableStyles.cellLeft}>&nbsp;</Text>
                                                            <Text
                                                                style={[tableStyles.cellLeft, tableStyles.totalCell, {flex: 3}]}>Ukupno {groupedRow.timberType.reportNameShort || groupedRow.timberType.name} klasa {groupedRow.timberClass.reportNameShort || groupedRow.timberClass.name}:</Text>
                                                            { filter.shipmentType != 'Cordwood' &&
                                                                <Text style={[tableStyles.cellRight, tableStyles.totalCell, {paddingRight: 0}]}>{groupedRow.count ?? '-'}</Text>
                                                            }
                                                            <Text style={[tableStyles.cellRight, tableStyles.totalCell]}>{fixedFormat(groupedRow.sum)}</Text>
                                                        </View>
                                                    </View>
                                                </React.Fragment>
                                            )}
                                            {storageRow.items
                                                .filter(a => !(a.items))
                                                .sort((a, b) => a.timberType.name.localeCompare(b.timberType.name) || a.timberClass.name.localeCompare(b.timberClass.name))
                                                .map((groupedRow, groupedRowIndex) =>
                                                <React.Fragment
                                                    key={'fragment' + storageRow.storage.id + groupedRow.timberClass.id + groupedRow.timberType.id}>
                                                    <View wrap={false}
                                                          key={'fragment' + storageRow.storage.id + groupedRow.timberClass.id + groupedRow.timberType.id}>
                                                        <View style={tableStyles.row}>
                                                            <Text style={tableStyles.cellLeft}>&nbsp;</Text>
                                                            <Text
                                                                style={[tableStyles.cellLeft, tableStyles.totalCell, {flex: 3}]}>Ukupno {groupedRow.timberType.reportNameShort || groupedRow.timberType.name} klasa {groupedRow.timberClass.reportNameShort || groupedRow.timberClass.name}:</Text>
                                                            { filter.shipmentType != 'Cordwood' &&
                                                                <Text style={[tableStyles.cellRight, tableStyles.totalCell, {paddingRight: 0}]}>{groupedRow.count ?? '-'}</Text>
                                                            }
                                                            <Text style={[tableStyles.cellRight, tableStyles.totalCell]}>{fixedFormat(groupedRow.sum)}</Text>
                                                        </View>
                                                    </View>
                                                </React.Fragment>
                                            )}
                                            <View wrap={false}>
                                                <View style={tableStyles.row}>
                                                    <Text
                                                        style={[tableStyles.cellLeftBold, tableStyles.totalCell, {flex: 5}]}>Ukupno skladiste {storageRow.storage.name} ({cuttingRow.cutting.name}):</Text>
                                                    { filter.shipmentType != 'Cordwood' &&
                                                        <Text style={[tableStyles.cellRightBold, tableStyles.totalCell]}>{storageRow.count ?? '-'}</Text>
                                                    }
                                                    <Text style={[tableStyles.cellRightBold, tableStyles.totalCell]}>{fixedFormat(storageRow.sum)}</Text>
                                                </View>
                                            </View>
                                        </React.Fragment>
                                    )}
                                        <View wrap={false}>
                                            <View style={tableStyles.row}>
                                                <Text
                                                    style={[tableStyles.cellLeftBold, tableStyles.totalCell, {flex: 5}]}>Ukupno sječa {cuttingRow.cutting.name}:</Text>
                                                { filter.shipmentType != 'Cordwood' &&
                                                    <Text style={[tableStyles.cellRightBold, tableStyles.totalCell]}>{cuttingRow.count ?? '-'}</Text>
                                                }
                                                <Text style={[tableStyles.cellRightBold, tableStyles.totalCell]}>{fixedFormat(cuttingRow.sum)}</Text>
                                            </View>
                                        </View>
                                    </React.Fragment>
                                )
                            }
                            <View wrap={false}>
                                <View style={tableStyles.row}>
                                    <Text style={[tableStyles.cellLeftBold, tableStyles.totalCell, {flex: 5}]}>Ukupno:</Text>
                                    { filter.shipmentType != 'Cordwood' &&
                                        <Text style={[tableStyles.cellRightBold, tableStyles.totalCell]}>{groupedData.count ?? '-'}</Text>
                                    }
                                    <Text style={[tableStyles.cellRightBold, tableStyles.totalCell]}>{fixedFormat(groupedData.sum)}</Text>
                                </View>
                            </View>
                        </View>
                    </View>
                </View>
                { numberOfCopies > 1 ?
                    <View fixed>
                        <Text style={styles.footer} render={({ pageNumber, totalPages }) => (
                            `Stranica ${pageNumber - (pagePosition - 1) * (totalPages / numberOfCopies)} / ${totalPages / numberOfCopies} | Kopija ${pagePosition} / ${numberOfCopies} | ${title}`
                        )} />
                    </View>
                    :
                    <View fixed>
                        <Text style={styles.footer} render={({ pageNumber, totalPages }) => (
                            `Stranica ${pageNumber - (pagePosition - 1) * (totalPages / numberOfCopies)} / ${totalPages / numberOfCopies} | ${title}`
                        )} />
                    </View>
                }
            </Page>)
            )}
        </Document>
    )
}

export default GenericRootReportPdf
